import { memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IPack } from "../services/packs/types";
import { DollarSign, Gift } from "lucide-react";
import { useTranslation } from "@/shared/i18n";

interface PackCardProps {
  pack: IPack;
  cover: string;
  published: boolean;
  name: string;
  creator: string;
  id?: string;
  description?: string;
  tags?: string[];
  presets?: Array<{
    id: string;
    image: string;
    name: string;
  }>;
  freePresetsCount?: number;
  paidPresetsCount?: number;
}

const getStatusStyles = (t: (key: string) => string) => ({
  published: {
    bg: "bg-[#8FD460]/10",
    text: "text-[#8FD460]",
    label: t('publishedLabel'),
  },
  not_published: {
    bg: "bg-[#91CAFF]/10",
    text: "text-[#91CAFF]",
    label: t('notPublishedLabel'),
  },
});

export const PackCard = memo(({
  pack,
  cover,
  published,
  name,
  creator,
  id,
  freePresetsCount = 0,
  paidPresetsCount = 0,
  description,
  tags = [],
  presets = [],
}: PackCardProps) => {
  const { t } = useTranslation('packs');
  const navigate = useNavigate();
  const status = published ? "published" : "not_published";
  const style = getStatusStyles(t)[status];

  const handleClick = useCallback(() => {
    navigate(`/packs/${pack.id || id}`, { state: { packData: pack } });
  }, [navigate, pack, id]);

  return (
    <article
      onClick={handleClick}
      className="group flex flex-col overflow-hidden rounded-lg border border-white/[0.06] hover:border-white/10 transition-colors cursor-pointer"
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden">
        <img
          src={cover}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          alt={name}
        />
      </div>
      <div className="flex flex-col gap-3 p-4">
        <span
          className={`inline-flex h-6 items-center justify-center rounded px-2 text-xs font-medium ${style.bg} ${style.text}`}
        >
          {style.label}
        </span>
        <div className="text-white">
          <h3 className="font-medium">{name}</h3>
          <p className="mt-1 text-sm text-white/60">{creator}</p>

          <div className="mt-3 pt-3 border-t border-white/5">
            <h4 className="text-xs font-medium text-white/40 mb-2">{t('presetsLabel')}</h4>
            <div className="flex flex-nowrap sm:flex-wrap items-center gap-x-4 text-sm text-white/80">
              {freePresetsCount !== undefined && (
                <span className="flex items-center gap-1.5">
                  <Gift className="h-3.5 w-3.5" />
                  {freePresetsCount} {t('freePresets')}
                </span>
              )}
              {paidPresetsCount !== undefined && (
                <span className="flex items-center gap-1.5">
                  <DollarSign className="h-3.5 w-3.5" />
                  {paidPresetsCount} {t('paidPresets')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
});

PackCard.displayName = "PackCard";
